import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { Mail, MessageSquare } from 'lucide-react';
import { authApiV2 } from '@/api/endpoints/auth';
import { dashboardApi } from '@/api/endpoints/dashboard';
import { dashboardAccessContextQueryKey } from '@/features/dashboard/hooks/useDashboardAccessContext';
import { useAuthStore } from '@/stores/auth.store';
import { AuthMarketingShell } from '@/features/auth/components/AuthMarketingShell';
import { AuthTextField } from '@/features/auth/components/AuthTextField';
import { runImpersonationHandoff } from '@/features/auth/lib/impersonation-handoff';
import { performSignOut } from '@/features/auth/lib/sign-out';
import {
  DEFAULT_POST_AUTH_PATH,
  loginRouteWithReturn,
  normalizePostAuthPath,
} from '@/lib/default-landing';
import { setMonitoringUser } from '@/lib/monitoring/identity';
import { getSupabaseClient, isSupabaseLoginEnabled } from '@/lib/supabase';
import type { TwoFactorMethod } from '@/types/two-factor';
import { cn } from '@/lib/cn';
import { RESEND_COOLDOWN_SECONDS } from '@/features/auth/lib/resend-cooldown';

function verifyErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const body = error.response?.data as
      | { error?: { code?: string; message?: string } }
      | undefined;
    const code = body?.error?.code;
    const message = body?.error?.message;
    const status = error.response?.status;
    if (code === 'locked') {
      return 'Your account is locked. Try again later or reset your password.';
    }
    if (code === 'rate_limited' || code === 'mfa_resend_limited' || status === 429) {
      return 'Too many verification attempts. Please wait a few minutes and try again.';
    }
    if (code === 'mfa_challenge_invalid') {
      return message ?? 'This verification session expired. Please sign in again.';
    }
    if (code === 'mfa_invalid') {
      return message ?? "That didn't match. Please try again.";
    }
    if (message) return message;
  }
  return "That didn't match. Please try again.";
}

/**
 * Page the email link asked for, captured before setUser clears the challenge.
 * Legacy verify-code uses the stored redirect after the code, and only falls
 * back to dashboard / properties / orders when that redirect is empty.
 */
function returnTargetAfterCode(
  pendingReturn: string | undefined,
  stateReturn: unknown,
  queryReturn: string | null,
): string {
  const fromState = typeof stateReturn === 'string' ? stateReturn : undefined;
  return normalizePostAuthPath(pendingReturn ?? fromState ?? queryReturn ?? undefined);
}

function isChallengeExpiredError(error: unknown): boolean {
  if (!isAxiosError(error)) return false;
  const body = error.response?.data as
    | { error?: { code?: string } }
    | undefined;
  return body?.error?.code === 'mfa_challenge_invalid';
}

/** Mask email for channel-picker copy (legacy protectEmail parity). */
function maskEmail(email: string): string {
  const [local, domain] = email.split('@');
  if (!domain || !local) return email;
  if (local.length <= 2) return `${local[0] ?? ''}***@${domain}`;
  return `${local.slice(0, 2)}***@${domain}`;
}

/**
 * /mfa/verify — login-time SMS/email OTP challenge (PRD §11/04, Auth V3).
 *
 * When both SMS and email are enrolled, shows a channel picker first (legacy
 * is2faSelectTypeForm). Single-channel accounts skip straight to code entry
 * because login already auto-sent the OTP.
 */
export function MfaVerifyPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const pending = useAuthStore((s) => s.pendingTwoFactor);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const { setTokens, setUser, clearPendingTwoFactor, setPendingTwoFactor } =
    useAuthStore();
  const [code, setCode] = useState('');
  const [rememberDevice, setRememberDevice] = useState(false);
  const [channel, setChannel] = useState<TwoFactorMethod>(
    pending?.method === 'sms' ? 'sms' : 'email',
  );
  const [codeSent, setCodeSent] = useState(() => pending?.codeSent === true);
  const [devSecret, setDevSecret] = useState<string | undefined>(
    () => pending?.devSecret,
  );
  const [resendCooldown, setResendCooldown] = useState(() =>
    pending?.codeSent ? RESEND_COOLDOWN_SECONDS : 0,
  );
  const [resendConfirmation, setResendConfirmation] = useState<string | null>(null);

  // Client-side resend cooldown countdown (DEF-233).
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const id = window.setInterval(() => {
      setResendCooldown((s) => (s <= 1 ? 0 : s - 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, [resendCooldown]);

  const verify = useMutation({
    mutationFn: async (input: { code: string; rememberDevice: boolean }) => {
      if (!pending?.mfaChallengeToken) {
        throw new Error('Missing MFA challenge');
      }
      return authApiV2.mfaChallengeVerify({
        mfaChallengeToken: pending.mfaChallengeToken,
        code: input.code,
        rememberDevice: input.rememberDevice,
      });
    },
    onSuccess: async (res) => {
      if (!res.accessToken || !res.refreshToken) {
        throw new Error('MFA verify response missing session tokens');
      }
      const stateReturn = (location.state as { returnTo?: unknown } | null)?.returnTo;
      const returnTo = returnTargetAfterCode(
        useAuthStore.getState().pendingTwoFactor?.returnTo,
        stateReturn,
        searchParams.get('returnTo'),
      );
      setTokens({ accessToken: res.accessToken, refreshToken: res.refreshToken });
      if (isSupabaseLoginEnabled()) {
        const { error } = await getSupabaseClient().auth.setSession({
          access_token: res.accessToken,
          refresh_token: res.refreshToken,
        });
        if (error) throw error;
      }
      const session = await authApiV2.me();
      setUser(session);
      setMonitoringUser(session);
      queryClient.setQueryData(['auth', 'me'], session);

      let handedOff = false;
      try {
        handedOff = await runImpersonationHandoff(session, { blockOnFailure: true });
        const after = useAuthStore.getState().user;
        if (after) setMonitoringUser(after);
        queryClient.removeQueries({ queryKey: ['teams', 'partners'] });
      } catch (err) {
        // AC-IMP-006/007: parked target failed — do not continue as SSI.
        await performSignOut();
        throw err;
      }

      queryClient.removeQueries({ queryKey: ['dashboard', 'access-context'] });
      await queryClient.fetchQuery({
        queryKey: dashboardAccessContextQueryKey(session.userId),
        queryFn: () => dashboardApi.getAccessContext(),
      });
      // Navigate first. setUser already dropped pendingTwoFactor; a render-time
      // "/login" bounce here is how a valid code looked like a failed 2FA (FR-356).
      // FR-555 — the email target survives the code. Impersonation still uses
      // the target person's default page.
      navigate(handedOff ? DEFAULT_POST_AUTH_PATH : returnTo, { replace: true });
      clearPendingTwoFactor();
    },
  });

  const sendCode = useMutation({
    mutationFn: async (nextChannel: TwoFactorMethod) => {
      if (!pending?.mfaChallengeToken) {
        throw new Error('Missing MFA challenge');
      }
      return authApiV2.mfaChallengeSend({
        mfaChallengeToken: pending.mfaChallengeToken,
        channel: nextChannel,
      });
    },
    onSuccess: (result, nextChannel) => {
      setChannel(nextChannel);
      setCode('');
      setCodeSent(true);
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      setResendConfirmation('A new code has been sent.');
      if (result.devSecret) setDevSecret(result.devSecret);
      if (pending) {
        setPendingTwoFactor({
          ...pending,
          mfaChallengeToken: result.mfaChallengeToken,
          method: nextChannel,
          phoneMasked: result.phoneMasked ?? pending.phoneMasked,
          codeSent: true,
          devSecret: result.devSecret,
        });
      }
    },
  });

  // Abandoned MFA (refresh / deep-link) goes back to sign in. A just-verified
  // session must not — setUser clears pendingTwoFactor (FR-356).
  useEffect(() => {
    if (pending) return;
    if (verify.isPending || verify.isSuccess) return;
    if (isAuthenticated) return;
    navigate(loginRouteWithReturn(searchParams.get('returnTo')), { replace: true });
  }, [pending, verify.isPending, verify.isSuccess, isAuthenticated, navigate, searchParams]);

  if (!pending) {
    return null;
  }

  function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (code.length !== 6) return;
    const trustDevice =
      (e.currentTarget.elements.namedItem('rememberDevice') as HTMLInputElement | null)
        ?.checked ?? rememberDevice;
    verify.mutate({ code, rememberDevice: trustDevice });
  }

  function handleBackToSignIn() {
    const stored = useAuthStore.getState().pendingTwoFactor?.returnTo;
    const stateReturn = (location.state as { returnTo?: unknown } | null)?.returnTo;
    const back = loginRouteWithReturn(
      stored ?? (typeof stateReturn === 'string' ? stateReturn : searchParams.get('returnTo')),
    );
    clearPendingTwoFactor();
    navigate(back, { replace: true });
  }

  const available = pending.availableChannels ?? [channel];
  const canSwitch = codeSent && available.length > 1;
  const otherChannel: TwoFactorMethod = channel === 'sms' ? 'email' : 'sms';
  const showChannelPicker = !codeSent && available.length > 1;
  const resendDisabled = sendCode.isPending || resendCooldown > 0;
  const challengeExpired =
    (verify.isError && isChallengeExpiredError(verify.error)) ||
    (sendCode.isError && isChallengeExpiredError(sendCode.error));

  if (showChannelPicker) {
    return (
      <AuthMarketingShell
        title="Verify your identity"
        subtitle="Choose how you'd like to receive your verification code."
        footer={
          <button
            type="button"
            onClick={handleBackToSignIn}
            className="font-medium text-primary hover:text-primary hover:underline"
          >
            Back to sign in
          </button>
        }
      >
        {sendCode.isError && (
          <div className="mb-4 rounded-md bg-destructive/10 px-4 py-2 text-sm text-destructive-text">
            {verifyErrorMessage(sendCode.error)}
            {isChallengeExpiredError(sendCode.error) && (
              <button
                type="button"
                onClick={handleBackToSignIn}
                className="mt-2 block text-xs underline underline-offset-2 hover:text-foreground"
              >
                Back to sign in
              </button>
            )}
          </div>
        )}

        <div className="flex flex-col gap-3">
          {available.includes('sms') && (
            <button
              type="button"
              disabled={sendCode.isPending}
              onClick={() => sendCode.mutate('sms')}
              className={cn(
                'flex items-center gap-4 rounded-lg border border-input bg-background px-4 py-4 text-left transition-colors hover:bg-accent hover:text-accent-foreground',
                'disabled:opacity-50',
              )}
            >
              <MessageSquare className="h-6 w-6 shrink-0 text-muted-foreground" />
              <div>
                <p className="font-medium text-foreground">Text message</p>
                <p className="text-xs text-muted-foreground">
                  Send to {pending.phoneMasked || 'your phone'}
                </p>
              </div>
            </button>
          )}
          {available.includes('email') && (
            <button
              type="button"
              disabled={sendCode.isPending}
              onClick={() => sendCode.mutate('email')}
              className={cn(
                'flex items-center gap-4 rounded-lg border border-input bg-background px-4 py-4 text-left transition-colors hover:bg-accent hover:text-accent-foreground',
                'disabled:opacity-50',
              )}
            >
              <Mail className="h-6 w-6 shrink-0 text-muted-foreground" />
              <div>
                <p className="font-medium text-foreground">Email</p>
                <p className="text-xs text-muted-foreground">
                  Send to {maskEmail(pending.email)}
                </p>
              </div>
            </button>
          )}
        </div>

        {sendCode.isPending && (
          <p className="mt-4 text-center text-xs text-muted-foreground">Sending code…</p>
        )}
      </AuthMarketingShell>
    );
  }

  return (
    <AuthMarketingShell
      title="Enter verification code"
      subtitle={
        channel === 'sms'
          ? `Sent to ${pending.phoneMasked || 'your phone'}`
          : `Sent to ${maskEmail(pending.email)}`
      }
      footer={
        <button
          type="button"
          onClick={handleBackToSignIn}
          className="font-medium text-primary hover:text-primary hover:underline"
        >
          Back to sign in
        </button>
      }
    >
      <div className="mb-5 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted-foreground/20">
          {channel === 'sms' ? (
            <MessageSquare className="h-5 w-5 text-muted-foreground" />
          ) : (
            <Mail className="h-5 w-5 text-muted-foreground" />
          )}
        </div>
        <p className="text-xs text-muted-foreground">
          {channel === 'sms' ? 'Text message verification' : 'Email verification'}
        </p>
      </div>

      {import.meta.env.DEV && devSecret && (
        <div className="mb-4 rounded-md border border-warning/30 bg-warning-bg px-3 py-2 text-xs text-warning">
          Local dev helper: your code is{' '}
          <span className="font-mono font-semibold tracking-wider">{devSecret}</span>
          . Check your inbox too — email may also have been sent.
        </div>
      )}

      <form onSubmit={onSubmit} autoComplete="off" className="flex flex-col gap-4">
        {(verify.isError || sendCode.isError) && (
          <div className="rounded-md bg-destructive/10 px-4 py-2 text-sm text-destructive-text">
            {verify.isError
              ? verifyErrorMessage(verify.error)
              : verifyErrorMessage(sendCode.error)}
            {challengeExpired && (
              <button
                type="button"
                onClick={handleBackToSignIn}
                className="mt-2 block text-xs underline underline-offset-2 hover:text-foreground"
              >
                Back to sign in
              </button>
            )}
          </div>
        )}

        {resendConfirmation && (
          <p className="text-xs text-primary" role="status" aria-live="polite">
            {resendConfirmation}
          </p>
        )}

        <div>
          <label htmlFor="mfa-code" className="mb-1.5 block text-sm font-medium text-foreground">
            6-digit code
          </label>
          <AuthTextField
            id="mfa-code"
            name="one-time-code"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            onClear={() => setCode('')}
            clearLabel="Clear verification code"
            placeholder="123456"
            inputClassName="text-center font-mono text-lg tracking-[0.5em]"
            autoFocus
          />
        </div>

        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          <input
            type="checkbox"
            name="rememberDevice"
            checked={rememberDevice}
            onChange={(e) => setRememberDevice(e.target.checked)}
            className="h-4 w-4 rounded border-input bg-background text-primary focus:ring-ring"
          />
          <span>Trust this device for 30 days</span>
        </label>

        <button
          type="submit"
          disabled={code.length !== 6 || verify.isPending}
          className={cn(
            'rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90',
            'disabled:opacity-50',
          )}
        >
          {verify.isPending ? 'Verifying…' : 'Verify'}
        </button>

        <div className="flex flex-col items-center gap-2 text-xs text-muted-foreground">
          <button
            type="button"
            disabled={resendDisabled}
            onClick={() => sendCode.mutate(channel)}
            className="transition-colors hover:text-foreground disabled:opacity-50"
          >
            {sendCode.isPending
              ? 'Sending…'
              : resendCooldown > 0
                ? `Resend code (${resendCooldown}s)`
                : 'Resend code'}
          </button>
          {canSwitch && available.includes(otherChannel) && (
            <button
              type="button"
              disabled={resendDisabled}
              onClick={() => sendCode.mutate(otherChannel)}
              className="transition-colors hover:text-foreground disabled:opacity-50"
            >
              {otherChannel === 'sms'
                ? 'Send code via SMS instead'
                : 'Send code via email instead'}
            </button>
          )}
          {available.length > 1 && (
            <button
              type="button"
              disabled={sendCode.isPending}
              onClick={() => setCodeSent(false)}
              className="transition-colors hover:text-foreground disabled:opacity-50"
            >
              Choose a different method
            </button>
          )}
        </div>
      </form>
    </AuthMarketingShell>
  );
}
